import classNames from 'classnames';
import styles from './Carousel.module.scss';
import { CarouselButton, useFade } from '..';
import { useRef, useEffect, useCallback, SetStateAction } from 'react';

interface CarouselProps {
  children: React.ReactNode;
  className?: string;
  step?: number;
  setIsScrollable?: (value: SetStateAction<boolean>) => void;
}

export default function Carousel({
  children,
  className,
  step = 300,
  setIsScrollable,
}: CarouselProps): JSX.Element {
  const contentRef = useRef<HTMLDivElement>(null);

  const handleContentScroll = useCallback(() => {
    if (contentRef.current && contentRef.current.scrollLeft <= 0) {
      setShowLeftButton(false);
    } else {
      setShowLeftButton(true);
    }

    if (
      contentRef.current &&
      contentRef.current.scrollLeft >=
        contentRef.current.scrollWidth - contentRef.current.offsetWidth
    ) {
      setShowRightButton(false);
    } else {
      setShowRightButton(true);
    }
  }, []);

  useEffect(() => {
    contentRef.current?.addEventListener('scroll', handleContentScroll);

    return () =>
      contentRef.current?.removeEventListener('scroll', handleContentScroll);
  }, [contentRef.current]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(([e]) => {
      if (e.target.scrollWidth === e.target.clientWidth) {
        setShowLeftButton(false);
        setShowRightButton(false);
      } else {
        handleContentScroll();
      }
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    handleContentScroll();
  }, [contentRef.current?.scrollWidth]);

  const handleLeftButtonClick = () => {
    if (contentRef.current) {
      let newScrollLeft;

      if (contentRef.current.scrollLeft > 1.5 * step) {
        newScrollLeft = contentRef.current.scrollLeft - step;
      } else {
        newScrollLeft = 0;
      }

      contentRef.current.scroll({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const handleRightButtonClick = () => {
    if (contentRef.current) {
      const remainingWidth =
        contentRef.current.scrollWidth -
        contentRef.current.offsetWidth -
        contentRef.current.scrollLeft;

      let newScrollLeft;

      if (remainingWidth > 1.5 * step) {
        newScrollLeft = contentRef.current.scrollLeft + step;
      } else {
        newScrollLeft = contentRef.current.scrollLeft + remainingWidth;
      }

      contentRef.current.scroll({
        left: newScrollLeft,
        behavior: 'smooth',
      });
    }
  };

  const [isLeftButtonVisible, , setShowLeftButton, LeftButtonFadeProps] =
    useFade(false);

  const [isRightButtonVisible, , setShowRightButton, RightButtonFadeProps] =
    useFade(true);

  useEffect(() => {
    if (!setIsScrollable) {
      return;
    }

    if (!isLeftButtonVisible && !isRightButtonVisible) {
      setIsScrollable(false);
    } else {
      setIsScrollable(true);
    }
  }, [isLeftButtonVisible, isRightButtonVisible]);

  return (
    <div className={classNames(styles.root, className && className)}>
      {isLeftButtonVisible && (
        <div
          className={styles.button}
          {...LeftButtonFadeProps}
        >
          <CarouselButton
            variant='left'
            onClick={handleLeftButtonClick}
          />
        </div>
      )}

      <div
        className={styles.content}
        ref={contentRef}
      >
        {children}
      </div>

      {isRightButtonVisible && (
        <div
          className={classNames(styles.button, styles.right)}
          {...RightButtonFadeProps}
        >
          <CarouselButton onClick={handleRightButtonClick} />
        </div>
      )}
    </div>
  );
}
